import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { ComplianceCard } from '@/components/dashboard/ComplianceCard';
import { ComplianceByRegionChart, ComplianceTrendChart } from '@/components/dashboard/Charts';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api-client';
import { formatDate, classNames } from '@/lib/utils';
import type { Store } from '@/types';

// Mock data para dashboard
const MOCK_STATS = {
    shareCompliance: 78,
    backlightCompliance: 85,
    totalStores: 5,
    auditedStores: 3,
    pendingAudits: 2,
    lastSync: new Date().toISOString(),
};

const MOCK_REGION_DATA = [
    { region: 'Sudeste', compliance: 85 },
    { region: 'Sul', compliance: 72 },
    { region: 'Nordeste', compliance: 90 },
    { region: 'Centro', compliance: 65 },
];

const MOCK_TREND_DATA = [
    { date: '01/01', compliance: 70 },
    { date: '05/01', compliance: 75 },
    { date: '10/01', compliance: 72 },
    { date: '15/01', compliance: 85 },
    { date: '20/01', compliance: 78 },
];

const MOCK_STORE_STATS: { store: Store; compliance: number; lastAudit?: string }[] = [
    { store: { id: 'store-001', name: 'Atacadão Centro', city: 'São Paulo', state: 'SP', hasBacklight: true, status: 'active' }, compliance: 92, lastAudit: '2026-01-18' },
    { store: { id: 'store-002', name: 'Assaí Zona Norte', city: 'São Paulo', state: 'SP', hasBacklight: false, status: 'active' }, compliance: 65, lastAudit: '2026-01-15' },
    { store: { id: 'store-003', name: 'Makro Industrial', city: 'Campinas', state: 'SP', hasBacklight: true, status: 'active' }, compliance: 78, lastAudit: '2026-01-10' },
];

export function Dashboard() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState(MOCK_STATS);
    const [storeStats, setStoreStats] = useState(MOCK_STORE_STATS);
    const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'all'>('month');

    useEffect(() => {
        // Simular carregamento
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-page-bg flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-page-bg pb-20">
            {/* Header */}
            <header className="bg-brand-header text-content-inverse sticky top-0 z-10">
                <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
                    <div>
                        <h1 className="text-lg font-semibold">Dashboard</h1>
                        <p className="text-sm opacity-80">Visão Geral</p>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                    </button>
                </div>
            </header>

            <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
                {/* Filtro de período */}
                <div className="flex gap-2">
                    {[
                        { value: 'week', label: 'Semana' },
                        { value: 'month', label: 'Mês' },
                        { value: 'all', label: 'Todo período' },
                    ].map((period) => (
                        <button
                            key={period.value}
                            onClick={() => setSelectedPeriod(period.value as typeof selectedPeriod)}
                            className={classNames(
                                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                selectedPeriod === period.value
                                    ? 'bg-brand-primary text-white'
                                    : 'bg-white border border-ui-border text-content-body hover:bg-brand-surface'
                            )}
                        >
                            {period.label}
                        </button>
                    ))}
                </div>

                {/* Cards de Compliance */}
                <div className="grid grid-cols-2 gap-4">
                    <ComplianceCard
                        title="Share of Shelf"
                        value={stats.shareCompliance}
                        subtitle={`${stats.auditedStores}/${stats.totalStores} lojas`}
                        icon={
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        }
                    />
                    <ComplianceCard
                        title="Backlight"
                        value={stats.backlightCompliance}
                        subtitle="Contrapartidas"
                        icon={
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                        }
                    />
                </div>

                {/* Gráficos */}
                <div className="space-y-4">
                    <ComplianceByRegionChart data={MOCK_REGION_DATA} />
                    <ComplianceTrendChart data={MOCK_TREND_DATA} />
                </div>

                {/* Resumo */}
                <Card>
                    <CardContent>
                        <h3 className="text-sm font-medium text-content-muted mb-3">Resumo</h3>
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                                <p className="text-2xl font-bold text-content-title">{stats.totalStores}</p>
                                <p className="text-xs text-content-muted">Total Lojas</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-status-success">{stats.auditedStores}</p>
                                <p className="text-xs text-content-muted">Auditadas</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-status-warning">{stats.pendingAudits}</p>
                                <p className="text-xs text-content-muted">Pendentes</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Lista de lojas com status */}
                <div>
                    <h3 className="text-sm font-medium text-content-muted mb-3">Lojas por Compliance</h3>
                    <div className="space-y-3">
                        {storeStats.map(({ store, compliance, lastAudit }) => (
                            <Card
                                key={store.id}
                                clickable
                                onClick={() => navigate(`/store/${store.id}`)}
                            >
                                <CardContent>
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className={classNames(
                                                    'w-3 h-3 rounded-full',
                                                    compliance >= 85 ? 'bg-status-success' :
                                                        compliance >= 70 ? 'bg-status-warning' :
                                                            'bg-status-danger'
                                                )} />
                                                <span className="font-medium text-content-title">{store.name}</span>
                                            </div>
                                            <p className="text-sm text-content-muted mt-1">
                                                {store.city}, {store.state}
                                                {lastAudit && ` • ${formatDate(lastAudit)}`}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <span className={classNames(
                                                'text-lg font-bold',
                                                compliance >= 85 ? 'text-status-success' :
                                                    compliance >= 70 ? 'text-status-warning' :
                                                        'text-status-danger'
                                            )}>
                                                {compliance}%
                                            </span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </main>

            {/* Bottom Navigation */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-ui-border safe-area-pb">
                <div className="max-w-lg mx-auto flex">
                    <button
                        className="flex-1 py-4 flex flex-col items-center text-content-muted"
                        onClick={() => navigate('/')}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        <span className="text-xs mt-1">Início</span>
                    </button>
                    <button className="flex-1 py-4 flex flex-col items-center text-brand-primary">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <span className="text-xs mt-1 font-medium">Dashboard</span>
                    </button>
                </div>
            </nav>
        </div>
    );
}

export default Dashboard;
